import { Dispatch } from 'react-redux';
import { StoreState } from '../../reducers';
import store from '../../store';
import setLocales from '../../store/setLocales';
import { Action } from '../../actions';
import { AppType } from './reducer';
import { openedFilesFolder } from '../../utils';
import { initializeMultiAppAuthentication } from '../MultiAppAuthentication/actions';
import { createNotification } from '../NotificationBar/actions';
import UserAgentUtils from '../../utils/userAgent';
import LocalStorageManager from '../../utils/localStorage/manager';
import NativeApi from '../../utils/nativeApi';
import AppTypeUtils from '../../utils/appType';
import IndexedDBManager from '../../utils/indexedDB/manager';
import { updateCredentials } from '../MultiAppAuthentication/actions';

enum AppInfosActionKeys {
	ACCEPT_COOKIE = '@@app/ACCEPT_COOKIE',
	SET_APP_TYPE = '@@app/SET_APP_TYPE',
	SET_USER_LOCALE = '@@app/SET_USER_LOCALE'
}

interface AcceptCookieAction extends Action {
	type: AppInfosActionKeys.ACCEPT_COOKIE;
}

interface SetAppTypeAppAction extends Action {
	type: AppInfosActionKeys.SET_APP_TYPE;
	appType: AppType;
}

interface SetAppUserLocaleAction extends Action {
	type: AppInfosActionKeys.SET_USER_LOCALE;
	locale: string;
}

type AppInfosAction = AcceptCookieAction
	| SetAppTypeAppAction
	| SetAppUserLocaleAction;

const getAppType = (): AppType => {
	return AppTypeUtils.isMultiApp ?
		AppType.Multiple :
		AppType.Single;
};

const initializeApp = () => async (dispatch: Dispatch<StoreState>): Promise<void | {}> => {
	try {
		const appType = getAppType();
		dispatch({ type: AppInfosActionKeys.SET_APP_TYPE, appType });

		// TODO: find a better way for removing openedFiles folder.
		// In current solution openedFiles folder exists and uses device space until next app launch
		// The best way to do it on app killing but there is no native api for it
		if (UserAgentUtils.isMobileApp) {
			const isOpenedFilesFolderExist = await NativeApi.existDirectory(openedFilesFolder);
			if (isOpenedFilesFolderExist) {
				NativeApi.deleteDirJs(`../../${openedFilesFolder}`);
			}

			await migrateGlobalLocalStorageToIndexedDB();

			const storedCredentials = await IndexedDBManager.getMultiAppCredentials();

			if (storedCredentials) {
				await dispatch(updateCredentials(storedCredentials));
			}
		}

		if (appType === AppType.Multiple) {
			await dispatch(initializeMultiAppAuthentication());
		}
	} catch (error) {
		dispatch(createNotification('error.initialization_fail'));
	}
};

const setUserLocale = (locale: string, hasPrefix: boolean, setStorage: boolean = true) => (dispatch: Dispatch<StoreState>): void => {
	if (setStorage) {
		IndexedDBManager.setUserLocale(locale, hasPrefix).then();
	}

	dispatch({ type: AppInfosActionKeys.SET_USER_LOCALE, locale });
	setLocales(store, locale);
};

const acceptCookie = (): AcceptCookieAction => ({
	type: AppInfosActionKeys.ACCEPT_COOKIE,
});

// At first login, move any existing global localStorage data to IndexedDB
const migrateGlobalLocalStorageToIndexedDB = async (): Promise<void> => {
	const wasIndexedDBInitialized = await IndexedDBManager.wasIndexedDBInitialized(false);

	if (!wasIndexedDBInitialized) {
		let allOk = true;

		const prevLocalStorageMultiAppCredentials = LocalStorageManager.getMultiAppCredentials();
		const prevLocalStorageGalleryConfig = LocalStorageManager.getStoreGalleryConfig();
		const prevLocalStorageProfileInfos = LocalStorageManager.getProfileInfos();
		const prevLocalStorageUserLocale = LocalStorageManager.getUserLocale(false);
		const prevLocalStorageMultiAppServer = LocalStorageManager.getMultiAppServer();

		if (prevLocalStorageMultiAppCredentials) {
			try {
				await IndexedDBManager.setMultiAppCredentials(prevLocalStorageMultiAppCredentials);
				LocalStorageManager.removeMultiAppCredentials();
			} catch (error) {
				console.error('Failed to migrate multi app credentials to IndexedDB', error);
				allOk = false;
			}
		}

		if (prevLocalStorageGalleryConfig) {
			try {
				await IndexedDBManager.setGalleryConfig(prevLocalStorageGalleryConfig);
				LocalStorageManager.removeStoreGalleryConfig();
			} catch (error) {
				console.error('Failed to migrate gallery config to IndexedDB', error);
				allOk = false;
			}
		}

		if (prevLocalStorageProfileInfos) {
			try {
				await IndexedDBManager.setProfileInfos(prevLocalStorageProfileInfos);
				LocalStorageManager.removeProfileInfos();
			} catch (error) {
				console.error('Failed to migrate profile infos to IndexedDB', error);
				allOk = false;
			}
		}

		if (prevLocalStorageUserLocale) {
			try {
				await IndexedDBManager.setUserLocale(prevLocalStorageUserLocale, false);
				LocalStorageManager.removeUserLocale(false);
			} catch (error) {
				console.error('Failed to migrate user locale to IndexedDB', error);
				allOk = false;
			}
		}

		if (prevLocalStorageMultiAppServer) {
			try {
				await IndexedDBManager.setMultiAppServer(prevLocalStorageMultiAppServer);
				LocalStorageManager.removeMultiAppServer();
			} catch (error) {
				console.error('Failed to migrate multi app server to IndexedDB', error);
				allOk = false;
			}
		}

		if (allOk) {
			await IndexedDBManager.setIndexedDBInitialized(false);
		} else {
			console.error('Failed to migrate global localStorage to IndexedDB');
		}
	}
};

export { AppInfosActionKeys, AppInfosAction, initializeApp, setUserLocale, acceptCookie };



// WEBPACK FOOTER //
// ./node_modules/tslint-loader!./src/containers/App/actions.ts